/**
 * A home é a BUSCA. Uma tela convidativa, estilo Google: "Me conte o que você quer saber."
 * Abaixo, os KPIs — os núcleos do cadastro e suas quantidades. Enviar a pergunta leva a `/busca`,
 * que interpreta a frase e responde com o recorte.
 *
 * O Enter BUSCA e o Ctrl+Enter quebra a linha — o contrário do que o `textarea` faz sozinho. A
 * caixa cresce com o conteúdo porque a pergunta pode ser uma frase, mas quem digita uma frase e
 * tecla Enter espera buscar, não ganhar uma linha.
 */
import {
  ArrowRight,
  Boxes,
  Building,
  Building2,
  Car,
  FileText,
  Home,
  Search,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { api, type PaginaCad } from '@/lib/api';
import { useSessao } from '@/lib/auth';
import { cn } from '@/lib/utils';

/** Um núcleo do cadastro: rótulo, endpoint de contagem, para onde leva e o ícone. */
type Nucleo = { nome: string; endpoint: string; para: string; icone: LucideIcon };

const NUCLEOS: Nucleo[] = [
  { nome: 'Pessoas', endpoint: '/cadastro/pessoas', para: '/cadastro', icone: Users },
  { nome: 'Empresas', endpoint: '/cadastro/empresas', para: '/cadastro/empresas', icone: Building2 },
  {
    nome: 'Condomínios',
    endpoint: '/cadastro/condominios',
    para: '/cadastro/condominios',
    icone: Building,
  },
  { nome: 'Imóveis', endpoint: '/cadastro/imoveis', para: '/cadastro', icone: Home },
  { nome: 'Veículos', endpoint: '/cadastro/veiculos', para: '/cadastro/veiculos', icone: Car },
  { nome: 'Processos', endpoint: '/cadastro/processos', para: '/cadastro/processos', icone: FileText },
];

export function Painel() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [pergunta, setPergunta] = useState('');
  const [totais, setTotais] = useState<Record<string, number> | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let vivo = true;
    // Cada núcleo tem seu total (a lista devolve `{itens, total}`); pedimos 1 item só pela contagem.
    void Promise.all(
      NUCLEOS.map(async (nu) => {
        const r = await api.get<PaginaCad<unknown>>(`${nu.endpoint}?limite=1`);
        return [nu.nome, r.ok ? r.dados.total : 0] as const;
      }),
    ).then((pares) => {
      if (vivo) setTotais(Object.fromEntries(pares));
    });
    return () => {
      vivo = false;
    };
  }, []);

  function submeter() {
    // A quebra vira ESPAÇO, não some. A caixa é multilinha, a busca é uma linha só: sem isto,
    // "aposentados⏎em Porto Alegre" chegava a `/busca` como «aposentadosem Porto Alegre» — o
    // resolvedor ainda acertava (ele separa por espaço em branco), mas a tela de resultados
    // devolvia a frase colada, e um Enter a partir dali buscaria a palavra emendada.
    const q = pergunta.replace(/\s+/g, ' ').trim();
    if (!q) return;
    // A busca tem tela própria: a home pergunta, `/busca` responde. Antes isto ia para
    // `/cadastro?busca=…`, e o termo se perdia duas vezes — no `Navigate` relativo e por ninguém
    // ler a query — de modo que a caixa da home levava à lista SEM filtro nenhum.
    navegar(`/busca?q=${encodeURIComponent(q)}`);
  }

  function enviar(e: FormEvent) {
    e.preventDefault();
    submeter();
  }

  /**
   * Enter BUSCA; Ctrl+Enter (ou ⌘+Enter) quebra a linha.
   *
   * É a inversão do padrão do `textarea`, e por isso a quebra tem de ser feita à mão: o navegador
   * não insere nada quando o Enter vem com modificador.
   *
   * `insertText` e não `setPergunta`: a inserção precisa ser SÍNCRONA. Recolocar o cursor depois,
   * num `requestAnimationFrame`, perde a tecla que vier no intervalo — digitando "aposentados
   * ⌃⏎ em Porto Alegre" o texto saía «aposentados⏎m Porto Alegree», com o "e" no fim. Isto aqui
   * insere no cursor, deixa o cursor depois da quebra, entra no desfazer do navegador e dispara o
   * `onChange` que já cuida do estado e da altura.
   */
  function aoTeclar(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter') return;
    // Enter que confirma candidato de IME (acento, teclado virtual) não é Enter de envio.
    if (e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (!(e.ctrlKey || e.metaKey)) return void submeter();
    document.execCommand('insertText', false, '\n');
  }

  // O textarea cresce com o conteúdo (até um teto), em vez de rolar num campo de uma linha.
  function ajustarAltura() {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }

  const primeiroNome = usuario?.nome.split(' ')[0] ?? '';
  const total = totais ? Object.values(totais).reduce((a, b) => a + b, 0) : 0;
  const comDados = total > 0;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-10 pt-[8vh]">
      {/* ---- Herói: a busca ---- */}
      <div className="flex w-full flex-col items-center gap-6 text-center">
        <h1 className="tipo-titulo">
          Me conte o que você quer saber{primeiroNome ? `, ${primeiroNome}` : ''}.
        </h1>

        <form onSubmit={enviar} className="w-full">
          {/* Sem borda estática: só a superfície e a sombra; o foco acende o anel âmbar. */}
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg bg-surface px-5 py-4 shadow-sm transition-shadow',
              'focus-within:shadow-[var(--focus-ring)]',
            )}
          >
            <Search className="mt-1 size-5 shrink-0 text-accent" />
            <textarea
              ref={areaRef}
              value={pergunta}
              onChange={(e) => {
                setPergunta(e.target.value);
                ajustarAltura();
              }}
              onKeyDown={aoTeclar}
              autoFocus
              rows={1}
              placeholder="um CPF, um telefone, uma placa — ou uma frase"
              className="min-w-0 flex-1 resize-none appearance-none border-0 bg-transparent p-0 text-[17px] leading-relaxed text-text shadow-none outline-none focus:ring-0 placeholder:text-text-tertiary"
            />
            <button
              type="submit"
              disabled={!pergunta.trim()}
              aria-label="Buscar"
              className={cn(
                'mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md',
                'bg-primary text-on-primary transition-colors hover:bg-primary-hover',
                'disabled:pointer-events-none disabled:opacity-40',
              )}
            >
              <ArrowRight className="size-5" />
            </button>
          </div>
        </form>

        <p className="tipo-corpo-pequeno text-text-tertiary">
          Ex.: “João da Silva” · “111.444.777-35” · “(51) 99112-2732”
        </p>
      </div>

      {/* ---- KPIs: os núcleos do cadastro e suas quantidades ---- */}
      <div className="w-full">
        <p className="tipo-rotulo mb-3">No cadastro</p>
        {totais === null ? (
          <div className="grid gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="h-24 animate-pulse bg-surface-2/60" />
            ))}
          </div>
        ) : comDados ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {NUCLEOS.map((nu) => {
              const Icone = nu.icone;
              return (
                <button
                  key={nu.nome}
                  type="button"
                  onClick={() => navegar(nu.para)}
                  className={cn(
                    'flex items-center gap-4 rounded-lg border border-border bg-surface',
                    'px-4 py-4 text-left shadow-sm transition-colors hover:border-border-strong hover:bg-surface-hover',
                  )}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface-2 text-accent">
                    <Icone className="size-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block tipo-dado text-[22px] leading-none text-text">
                      {(totais[nu.nome] ?? 0).toLocaleString('pt-BR')}
                    </span>
                    <span className="mt-1 block truncate tipo-corpo-pequeno text-text-secondary">
                      {nu.nome}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <Card className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <Boxes className="size-6 text-text-tertiary" />
            <p className="tipo-corpo-grande text-text">O cadastro ainda está vazio.</p>
            <p className="tipo-corpo-pequeno text-text-secondary">
              Cadastre uma pessoa ou ingira uma planilha em{' '}
              <button
                type="button"
                onClick={() => navegar('/cadastro')}
                className="text-accent hover:underline"
              >
                Cadastro
              </button>{' '}
              para o motor começar a correlacionar.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
